import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import api from '../../../services/api';
import CompanyEditModal from '../../../components/modals/CompanyEditModal';
import UserEditModal from '../../../components/modals/UserEditModal';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import { maskBr } from 'js-brasil';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthContext } from '../../../context/AuthContext';

export default function SuperAdminCompanies() {
  const { user: currentUser } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [nameFilter, setNameFilter] = useState('');
  const [cnpjFilter, setCnpjFilter] = useState('');

  // 1. fetchCompanies com useCallback para silenciar o aviso de dependência
  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/companies', {
        params: { userId: currentUser?.id },
      });
      res.data.sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(res.data);
    } catch (err) {
      setError('Falha ao carregar empresas.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // 2. Limpeza do scroll ao desmontar
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleOpenModal = (company = null) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenUserModal = user => {
    setUserToEdit(user);
    setIsUserModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setUserToEdit(null);
    document.body.style.overflow = '';
  };

  const handleSaveCompany = async companyData => {
    const slugExists = companyData.slug;

    if (!slugExists) {
      const baseSlug = companyData.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]+/g, '')
        .replace(/^-+|-+$/g, '');

      companyData.slug = `${baseSlug}-${companyData.cnpjOrTaxId?.slice(0, 8)}`;
    }

    try {
      if (selectedCompany?.id) {
        await api.put(
          `/companies/${slugExists ? selectedCompany.slug : selectedCompany.id}`,
          companyData
        );
      } else {
        const response = await api.post('/companies', companyData);
        const { newAdmin } = response.data;
        if (newAdmin) {
          handleOpenUserModal(newAdmin);
        }
      }
      await fetchCompanies();
      handleCloseModal();
    } catch (err) {
      const errorMessage = err.response?.data?.msg || 'Falha ao salvar empresa.';
      alert(errorMessage);
    }
  };

  const handleSaveUser = async dataToSave => {
    try {
      if (userToEdit?.id) {
        await api.put(`/users/${userToEdit.userName}`, dataToSave);
        await fetchCompanies(); // Usa a função memorizada
      }
      handleCloseUserModal();
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
      throw err;
    }
  };

  const handleDeleteClick = company => {
    setItemToDelete(company);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setItemToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async company => {
    try {
      await api.delete(`/companies/${company.slug}`);
      setCompanies(prev => prev.filter(c => c.id !== company.id));
      handleCloseDeleteModal();
    } catch (err) {
      alert(`Erro: ${err.response?.data?.message || 'Falha ao excluir empresa.'}`);
    }
  };

  // 3. Filtro memorizado para melhor performance e evitar warnings
  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const tradeNameMatch =
        !nameFilter || company.name?.toLowerCase().includes(nameFilter.toLowerCase());
      const cnpjMatch =
        !cnpjFilter ||
        company.cnpjOrTaxId?.replace(/[^\d]/g, '').includes(cnpjFilter.replace(/[^\d]/g, ''));
      const roleMatch =
        currentUser?.role === 'ADMIN_CLIENT'
          ? company.admins?.some(admin => admin.id === currentUser.id)
          : true;
      return tradeNameMatch && cnpjMatch && roleMatch;
    });
  }, [companies, nameFilter, cnpjFilter, currentUser]);

  if (loading)
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div> Carregando empresas...
      </div>
    );
  if (error) return <div className="error-message">{error}</div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card-body">
          <h3 className="filter-title">Filtros de Empresas</h3>
          <div
            className="filter-form"
            style={{ display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }}
          >
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="name-filter" className="form-label">
                Buscar por Nome
              </label>
              <input
                id="name-filter"
                type="text"
                className="form-input"
                placeholder="Nome da empresa..."
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cnpj-filter" className="form-label">
                Buscar por CNPJ
              </label>
              <input
                id="cnpj-filter"
                type="text"
                className="form-input"
                placeholder="00.000.000/0001-00"
                value={cnpjFilter}
                onChange={e => setCnpjFilter(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-4)',
            }}
          >
            <h2>Todas as Empresas ({filteredCompanies.length})</h2>
            {(currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'ADMIN_EAD') && (
              <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                + Criar Nova Empresa
              </button>
            )}
          </div>
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Admin Responsável</th>
                  <th>Plano</th>
                  <th style={{ width: '1%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map(company => (
                  <tr
                    key={company.slug}
                    onClick={() => handleOpenModal(company)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{company.name}</td>
                    <td>
                      {company.cnpjOrTaxId ? maskBr.cnpj(company.cnpjOrTaxId) : 'Não informado'}
                    </td>
                    <td>{company.admins?.map(a => a.fullName).join(', ') || 'Não informado'}</td>
                    <td>{company.subscribedPlan?.name || 'Não informado'}</td>
                    <td>
                      <div className="table-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenModal(company)}
                        >
                          Editar
                        </button>
                        {(currentUser?.role === 'SUPER_ADMIN' ||
                          currentUser?.role === 'ADMIN_EAD') && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteClick(company)}
                            title="Excluir"
                          >
                            <DeleteIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <CompanyEditModal
          company={selectedCompany}
          onClose={handleCloseModal}
          onSave={handleSaveCompany}
          currentUser={currentUser}
        />
      )}

      {isUserModalOpen && (
        <UserEditModal
          user={userToEdit}
          onClose={handleCloseUserModal}
          onSuccess={handleSaveUser}
          companies={companies}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirmSuccess={handleConfirmDelete}
          itemType="company"
          item={itemToDelete}
        />
      )}
    </>
  );
}
